import { useEffect } from 'react';
import { utilityGetPageState } from '@utils/get-page-state';
import { useStoreActions, useStoreState } from '@app/store/hooks/index';
import {
  jobActualStoreSelector,
  jobProgressSelector,
  setCurrentJobIdSelector,
} from '@model/Job/Job.selectors';
import { contractStoreStateSelector } from '@model/Contract/Contract.selectors';
import {
  setCurrentRouteSelector,
  setCurrentStatusSelector,
} from '@app/store/selectors/common-selectors';
import { useNavigate, useParams } from 'react-router-dom';
import type { TJobProgressSwitchParams } from '@components/navigation/JobProgressSwitch/JobProgressSwitch.types';
import { useJobProgressSegment } from '@components/navigation/JobProgressSwitch/_hooks/use-job-progress-segment';

export const useEntityNavigation = (): void => {
  // STATE
  const initialized: boolean = useStoreState(state => state.initialized);
  const job = useStoreState(jobActualStoreSelector);
  const contract = useStoreState(contractStoreStateSelector);
  const jobProgress = useStoreState(jobProgressSelector);

  // ACTIONS
  const setCurrentStatus = useStoreActions(setCurrentStatusSelector);
  const setCurrentRoute = useStoreActions(setCurrentRouteSelector);
  const setCurrentJobId = useStoreActions(setCurrentJobIdSelector);

  // ROUTER
  const navigate = useNavigate();
  const segment = useJobProgressSegment();
  const { entityType, entityId } = useParams<TJobProgressSwitchParams>();

  const updatePageStatus = (): void | Promise<void> => {
    if (!initialized || !entityId) {
      return;
    }

    setCurrentJobId(entityId);

    if (!segment) {
      return navigate(`/${entityType}/${entityId}/${jobProgress}`);
    }

    setCurrentStatus(
      utilityGetPageState({
        progress: jobProgress,
        currentRoute: segment,
      }),
    );

    setCurrentRoute(segment);
  };

  useEffect(() => {
    if (contract?.jobId && !job) {
      window.location.assign(
        `${__ENVIRONMENT_CONFIG__.PUBLIC_URL}/jobs/${contract.jobId}/${
          segment ?? ''
        }`,
      );
    }
  }, [contract, job]);

  useEffect(() => {
    updatePageStatus();
  }, [initialized, entityId, segment, jobProgress]);
};
